import React from 'react';
import classNames from 'classnames';

import { NamedLink } from '../../components';
import CompactListenerCard from '../CompactListenerCard/CompactListenerCard';
import { normalizePaths, pathLabels, SEP, LEGACY_TO_PATH, rememberTopicPath } from '../../util/topicPaths';
import {
  SESSION_TOPIC_LABELS,
  CONVERSATION_TOPIC_IDS,
  INTEREST_IDS,
  OTHER_TOPIC_ID,
  topicsFromListing,
  favoriteTeamsFromListing,
  isInterestId,
  normalizeTopics,
} from '../../util/sessionTopics';

import css from './TalkAbout.module.css';

/** Light, concrete starters. Each opens Browse filtered by its chip. */
export const CONVERSATION_STARTERS = [
  { id: 'sports', text: 'Want to talk last night’s game?' },
  { id: 'work-career', text: 'Need to vent about work?' },
  { id: 'binge-shows', text: 'Binged a Netflix series this weekend?' },
  { id: 'tech', text: 'Curious what AI means for your job?' },
  { id: 'news-current-events', text: 'Want to talk about what’s happening in the world?' },
  { id: 'pets', text: 'Got a pet story to share?' },
  { id: 'dating', text: 'Dating app stories to tell?' },
  { id: 'breakups', text: 'Getting over a breakup?' },
  { id: 'money-worries', text: 'Money stress on your mind?' },
  { id: 'career-advice', text: 'Thinking about a career move?' },
  { id: 'music', text: 'Found a song you can’t stop playing?' },
];

/** Home hero chips (age-neutral copy): the hero chips on the home page (each can cover several ids). */
export const HOME_HERO_CHIPS = [
  { key: 'dating', icon: '❤️', line: 'Back in the dating world, or starting over?', label: 'Dating & divorce', ids: ['dating', 'divorce-separation', 'breakups'] },
  { key: 'career', icon: '💼', line: 'Friendly advice from people who’ve been there.', label: 'Career & mentorship', ids: ['career-advice', 'mentorship', 'work-career'] },
  { key: 'money', icon: '💵', line: 'Money stress keeping you up?', label: 'Money stress', ids: ['money-worries'] },
  { key: 'sports', icon: '🏈', line: 'Talk last night’s game with a fellow fan.', label: 'Sports', ids: ['sports'] },
  { key: 'world', icon: '🌎', line: 'Civil conversation, not a comment pile-on.', label: 'World events', ids: ['news-current-events'] },
];

export const HOME_STARTERS = [
  { id: 'dating', text: 'Back in the dating world?' },
  { id: 'career-advice', text: 'Want friendly advice from someone who’s been there?' },
  { id: 'money-worries', text: 'Money stress keeping you up?' },
  { id: 'sports', text: 'Want to talk last night’s game?' },
  { id: 'news-current-events', text: 'Want to talk about the news without the shouting?' },
];

/** Chips shown first on home / Browse quick row. */
export const FEATURED_CHIP_IDS = [
  'sports',
  'work-career',
  'tv-movies',
  'binge-shows',
  'pets',
  'relationships-dating',
  'dating',
  'music',
  'parenting',
  'gaming',
  'breakups',
  'cooking-food',
  'money-worries',
  'true-crime',
  'tech',
  'news-current-events',
  'faith-church',
  'major-life-changes',
];

const searchFor = id => ({ search: `?topics=${encodeURIComponent(id)}` });

export const ConversationStarters = ({ className, limit = 8, starters = CONVERSATION_STARTERS }) => (
  <ul className={classNames(css.starters, className)} data-conversation-starters="true">
    {starters.slice(0, limit).map(s => (
      <li key={s.id}>
        <NamedLink name="SearchPage" to={searchFor(s.id)} className={css.starter}>
          {s.text}
        </NamedLink>
      </li>
    ))}
  </ul>
);

/**
 * Chip row. If onSelect is given chips are buttons (inline filter), otherwise
 * links to /s?topics=id.
 */
export const TalkAboutChips = props => {
  const { ids = FEATURED_CHIP_IDS, selected, onSelect, className, activeParam = [] } = props;
  return (
    <ul className={classNames(css.chips, className)} data-talk-about-chips="true">
      {ids.map(id => {
        const on = selected === id || activeParam.includes(id);
        const cls = classNames(css.chip, {
          [css.chipInterest]: isInterestId(id),
          [css.chipOn]: on,
        });
        return (
          <li key={id}>
            {onSelect ? (
              <button type="button" className={cls} aria-pressed={on} onClick={() => onSelect(on ? null : id)}>
                {SESSION_TOPIC_LABELS[id] || id}
              </button>
            ) : (
              <NamedLink name="SearchPage" to={searchFor(id)} className={cls}>
                {SESSION_TOPIC_LABELS[id] || id}
              </NamedLink>
            )}
          </li>
        );
      })}
    </ul>
  );
};

/** Split a listing's topics into conversation topics and interests for display. */
export const lovesToChatAbout = listing => {
  const ids = topicsFromListing(listing).filter(id => id !== OTHER_TOPIC_ID);
  return {
    interests: ids.filter(isInterestId),
    topics: ids.filter(id => !isInterestId(id)),
    teams: favoriteTeamsFromListing(listing),
  };
};

/**
 * "Loves to chat about" on a listener profile. Each chip opens /s?mode=search with that topic
 * path selected (Sam, Oct 15). Never opens the booking/status panel.
 */
export const LovesToChatAbout = ({ listing, className }) => {
  const { interests, topics, teams } = lovesToChatAbout(listing);
  const specific = normalizePaths(listing?.attributes?.publicData?.topicPaths).filter(p => p.split('/').length > 2);
  if (!interests.length && !topics.length && !specific.length) return null;
  const chips = [
    ...specific.map(p => ({ key: p, path: p, label: pathLabels(p).slice(1).join(SEP), interest: true })),
    ...[...interests, ...topics].map(t => ({ key: t, path: LEGACY_TO_PATH[t], label: SESSION_TOPIC_LABELS[t] || t, interest: isInterestId(t) })),
  ];
  return (
    <section className={classNames(css.loves, className)} data-loves-to-chat="true">
      <h3 className={css.lovesTitle}>Loves to chat about</h3>
      <p className={css.lovesHint}>Tap a topic to see listeners who talk about it.</p>
      <ul className={css.chips}>
        {chips.map(c => (
          <li key={c.key}>
            {c.path || c.key ? (
              <NamedLink
                name="SearchPage"
                to={{ search: c.path ? `?mode=search&paths=${encodeURIComponent(c.path)}` : `?mode=search&topics=${encodeURIComponent(c.key)}` }}
                className={classNames(css.chip, { [css.chipInterest]: c.interest })}
                onClick={() => c.path && rememberTopicPath(c.path)}
                data-search-topic={c.path || c.key}
              >
                {c.label}
              </NamedLink>
            ) : (
              <span className={css.chip}>{c.label}</span>
            )}
          </li>
        ))}
      </ul>
      {teams ? (
        <p className={css.teams}>
          <span aria-hidden="true">🏟️ </span>Favorite teams: {teams}
        </p>
      ) : null}
    </section>
  );
};

/**
 * "We think you'd like these listeners" — rows from rankListingsByOverlap.
 * Talker interests are used only for matching; we show the shared tags, never the talker's list.
 */
export const MatchRow = ({
  rows = [],
  title = 'Listeners we think you’ll like',
  sub = 'Based on what you like to talk about. Available now first.',
  className,
}) => {
  if (!rows.length) return null;
  return (
    <section className={classNames(css.matchRow, className)} data-match-row="true">
      <h2 className={css.matchTitle}>{title}</h2>
      <p className={css.matchSub}>{sub}</p>
      <ul className={css.matchList}>
        {rows.map(({ listing, online }) => (
          <li key={listing.id.uuid} className={css.matchCard}>
            <CompactListenerCard listing={listing} online={online} />
          </li>
        ))}
      </ul>
    </section>
  );
};

export const ALL_GROUPS = { topics: CONVERSATION_TOPIC_IDS, interests: INTEREST_IDS };

/** Tips shown above the listener's topics + interests picker. */
export const InterestTips = () => (
  <div className={css.tips} data-interest-tips="true">
    <strong>Tips: be specific ✨</strong>
    <ul>
      <li>Pick the interests you could happily chat about for 15 minutes.</li>
      <li>Add your teams if you pick Sports. “Eagles” gets found, “sports fan” doesn’t.</li>
      <li>Mention a show, a pet or a hobby in your About text too. It gives talkers an easy opener.</li>
      <li>Keep it light. Heavy topics belong with a licensed professional.</li>
    </ul>
  </div>
);

/** Compact "Loves to chat about" line for listener cards (inside a link, so plain spans). */
export const CardLovesToChat = ({ listing, limit = 4 }) => {
  const { interests, topics, teams } = lovesToChatAbout(listing);
  const ids = [...interests, ...topics].slice(0, limit);
  if (!ids.length) return null;
  return (
    <div className={css.cardLoves} data-card-loves="true">
      <span className={css.cardLovesLabel}>Loves to chat about</span>
      <span className={css.cardChips}>
        {ids.map(id => (
          <span key={id} className={classNames(css.cardChip, { [css.chipInterest]: isInterestId(id) })}>
            {SESSION_TOPIC_LABELS[id] || id}
          </span>
        ))}
      </span>
      {teams ? <span className={css.cardTeams}>🏟️ {teams}</span> : null}
    </div>
  );
};

/** Talker → listener prompt using their own interests (private; only shown to them). */
export const BecomeListenerPrompt = ({ talkerTopics = [] }) => {
  const first = talkerTopics.find(id => id !== OTHER_TOPIC_ID);
  const label = first ? SESSION_TOPIC_LABELS[first] : null;
  return (
    <section className={css.crossPrompt} data-become-listener-prompt="true">
      <h2 className={css.crossTitle}>
        {label ? `Love talking about ${label}? Get paid to listen.` : 'Love a good conversation? Get paid to listen.'}
      </h2>
      <p className={css.crossText}>
        Meet people like you. Here everyone talks and listens: chat about the things you love with
        someone who shares them, and earn for every completed session.
      </p>
      <NamedLink name="BecomeAListenerPage" className={css.crossCta}>
        Become a listener too →
      </NamedLink>
    </section>
  );
};

/** "Topic: Sports, Dating" on booking cards (talker + listener). Hidden when none was picked. */
export const BookingTopicsLine = ({ tx, className }) => {
  const pd = tx?.attributes?.protectedData || {};
  const pathText = typeof pd.topicPathLabel === 'string' ? pd.topicPathLabel.trim() : '';
  const ids = normalizeTopics(pd.topicsToday).filter(id => id !== OTHER_TOPIC_ID);
  const text = pathText || ids.map(id => SESSION_TOPIC_LABELS[id] || id).join(', ');
  const note = typeof pd.topicNote === 'string' ? pd.topicNote.trim() : '';
  if (!text && !note) return null;
  return (
    <div className={classNames(css.bookingTopics, className)} data-booking-topics={pd.topicPath || ids.join(',')}>
      {text ? (
        <p className={css.bookingTopicsRow}>
          <span className={css.bookingTopicsLabel}>Topic:</span> {text}
        </p>
      ) : null}
      {note ? (
        <p className={css.bookingTopicsRow} data-booking-note="true">
          <span className={css.bookingTopicsLabel}>Note:</span> <span className={css.bookingNote}>“{note}”</span>
        </p>
      ) : null}
    </div>
  );
};
